import React, { useCallback, useEffect, useState } from 'react';

import * as Yup from 'yup';

import { FormHandles } from '@unform/core';

import { AxiosError } from 'axios';

import styled from 'styled-components';
import { darken } from 'polished';
import { FiHelpCircle } from 'react-icons/fi';
import Switch from '../../../../../../newComponents/Switch';
import Input from '../../../../../../newComponents/UnformInput';
import Select from '../../../../../../newComponents/UnformSelect';

import { Grid, usePizzaProduct } from '../../../../../../hooks/pizzaProduct';
import { useToast } from '../../../../../../hooks/toast';
import { useAuth } from '../../../../../../hooks/auth';

import {
  Container,
  Header,
  SizesList,
  SizeItem,
  SizeItemHeader,
  SizeItemBody,
  HelpButton,
} from './styles';
import { CustomInput } from '../../../../styles';

import ProductsService from '../../../../../../services/ProductsService';

import { newDebounce } from '../../../../../../utils/debounce';

export const Help = styled.div`
  position: relative;
  cursor: help;

  div {
    visibility: hidden;
    position: absolute;
    background-color: ${({ theme }) => theme.colors.black};
    text-align: center;
    border-radius: 6px;
    padding: 10px 20px;
    position: absolute;
    z-index: 5;
    width: max-content;
    top: 150%;
    left: 60%;
    transform: translateX(-50%);
    max-width: 400px;

    &::after {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 50%;
      margin-left: -5px;
      border-width: 5px;
      border-style: solid;
      border-color: transparent transparent ${({ theme }) => theme.colors.black}
        transparent;
    }

    span {
      white-space: pre-wrap;
      font-weight: 400;
      color: ${({ theme }) => darken(0.05, theme.colors.lightGrey)};
    }

    strong {
      display: block;
      margin-top: 10px;
      color: ${({ theme }) => theme.colors.white};
      font-weight: 700;
      font-size: 14px;
    }
  }

  &:hover div {
    visibility: visible;
  }

  svg {
    font-size: 18px;
    color: ${({ theme }) => theme.colors.grey};
  }
`;

export const SizesFormSchema = Yup.object().shape({
  grids: Yup.array().of(
    Yup.object().shape({
      code: Yup.string().max(40, 'No máximo 20 caracteres'),
      price: Yup.number()
        .required('O preço é obrigatório')
        .transform((_, value) => (value ? Number(value) : 0)),
      discount: Yup.number()
        .integer('Desconto inválido')
        .min(0, 'Desconto inválido')
        .max(99.99, 'Desconto inválido')
        .required('O desconto é obrigatório')
        .transform((_, value) => (value ? Number(value) : 0)),
      minimumStock: Yup.number()
        .min(0, 'Estoque mínimo inválido')
        .required('O estoque mínimo é obrigatório')
        .transform((_, value) =>
          value === '' || value === null || value === undefined
            ? 0
            : Number(value),
        ),
      // Estoque atual pode ficar negativo após vendas; igual ao produto simples.
      currentStock: Yup.number()
        .required('O estoque atual é obrigatório')
        .transform((_, value) =>
          value === '' || value === null || value === undefined
            ? 0
            : Number(value),
        ),
      addStock: Yup.number().transform((_, value) =>
        value === '' || value === null || value === undefined
          ? 0
          : Number(value),
      ),
    }),
  ),
});

type SizesProps = {
  formRef: React.RefObject<FormHandles>;
};

const Sizes: React.FC<SizesProps> = (props) => {
  const { formRef } = props;
  const {
    id,
    sizes,
    grids,
    display,
    handleChangeDisplay,
    selectedGrid,
    numOfFlavors,
    handleChangeSelectedNumOfFlavors,
    handleChangeSelectedGrid,
    handleEditGrid,
    handleToggleCanChangeCurrentStock,
  } = usePizzaProduct();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [codeIsValid, setCodeIsValid] = useState(grids.map(() => false));
  const [codeIsLoading, setCodeIsLoading] = useState(grids.map(() => false));

  useEffect(() => {
    if (grids.length > 0) {
      setCodeIsValid(grids.map(() => false));
      setCodeIsLoading(grids.map(() => false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grids.length]);

  const req = async (code: string, gridIndex: number): Promise<void> => {
    formRef.current?.setErrors({});
    setCodeIsValid((oldState) => oldState.map(() => false));
    setCodeIsLoading((oldState) =>
      oldState.map((_, index) => index === gridIndex),
    );

    if (code) {
      if (
        grids.some(
          (_, index) =>
            formRef.current?.getFieldValue(`grids[${index}].code`) === code &&
            index !== gridIndex,
        )
      ) {
        formRef.current?.setErrors({
          [`grids[${gridIndex}].code`]: 'Você já escolheu este código',
        });
        setCodeIsLoading((oldState) =>
          oldState.map((value, index) => (index === gridIndex ? false : value)),
        );
        return;
      }

      try {
        const data = {
          code,
        };

        if (typeof grids[gridIndex].id === 'number') {
          Object.assign(data, {
            productPizzaId: grids[gridIndex].id,
          });
        }

        await ProductsService.validatePDVCode(user.empresa_id, data);

        setCodeIsValid((oldState) =>
          oldState.map((_, index) => index === gridIndex),
        );
        setCodeIsLoading((oldState) =>
          oldState.map((value, index) => (index === gridIndex ? false : value)),
        );
      } catch (err) {
        console.log(err);
        formRef.current?.setErrors({
          [`grids[${gridIndex}].code`]: 'Código já em uso',
        });
        addToast({
          title: (err as AxiosError).message,
          type: 'error',
        });
        setCodeIsLoading((oldState) =>
          oldState.map((value, index) => (index === gridIndex ? false : value)),
        );
      }
    } else {
      setCodeIsLoading((oldState) =>
        oldState.map((value, index) => (index === gridIndex ? false : value)),
      );
    }
  };

  const handleChangeGridStatus = useCallback(
    (grid: Grid, status: boolean) => {
      handleEditGrid({ ...grid, active: status });
    },
    [handleEditGrid],
  );

  const reqDebouce: (code: string, gridIndex: number) => void = newDebounce(
    async (code: string, gridIndex: number): Promise<void> =>
      req(code, gridIndex),
    2000,
  );

  return (
    <Container>
      <Header>
        <h2>Tamanhos</h2>
        <p>
          Aqui você pode definir em quais tamanhos este produto estará
          disponível
        </p>
      </Header>
      <label
        htmlFor="display"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          marginBottom: 15,
        }}
      >
        <Switch
          id="display"
          checked={display}
          onChange={handleChangeDisplay}
          height={24}
          width={48}
          handleDiameter={15}
          disabled={!!id}
        />
        <h3 style={{ marginLeft: 10 }}>Pizza multi sabores</h3>
        <Help>
          <div>
            <span>O que é pizza multi sabores:</span>
            <strong>
              Ao ativar esta opção, permite-se a seleção de diversos sabores em
              uma única pizza, onde o valor do produto dependerá dos itens já
              cadastrados.
            </strong>
          </div>
          <FiHelpCircle />
        </Help>
      </label>
      {display ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 15,
            height: '100%',
          }}
        >
          <div>
            <Select
              id="sizeDisplay"
              name="sizeDisplay"
              label="Tamanho"
              value={sizes
                .map((size) => ({
                  value: size.id,
                  label: size.name,
                }))
                .find((size) => size.value === selectedGrid)}
              placeholder="Selecione uma opção"
              onChange={(option: any) => {
                if (option) {
                  handleChangeSelectedGrid(option.value);
                }
              }}
              options={sizes
                .sort((a, b) => a.order - b.order)
                .map((size) => ({
                  value: size.id,
                  label: size.name,
                }))}
            />
          </div>
          <div>
            {selectedGrid && (
              <Select
                id="numOfFlavors"
                name="numOfFlavors"
                label="Sabores"
                placeholder="Selecione uma opção"
                value={{
                  value: numOfFlavors,
                  label: numOfFlavors,
                }}
                onChange={(option: any) => {
                  if (option) {
                    handleChangeSelectedNumOfFlavors(option.value);
                  }
                }}
                options={Array.from(
                  {
                    length:
                      sizes.find((size) => size.id === selectedGrid)
                        ?.numberOfFlavors || 0,
                  },
                  (_, i) => ({ value: i + 1, label: i + 1 }),
                )}
              />
            )}
          </div>
        </div>
      ) : (
        <SizesList>
          {grids
            .sort((a, b) => a.order - b.order)
            .map((grid, gridIndex) => (
              <SizeItem key={grid.id}>
                <SizeItemHeader>
                  <h3>{grid.name}</h3>
                  <Switch
                    checked={grid.active}
                    onChange={(checked) =>
                      handleChangeGridStatus(grid, checked)
                    }
                    height={24}
                    width={48}
                    handleDiameter={15}
                  />
                </SizeItemHeader>
                <SizeItemBody>
                  <div>
                    <Input
                      name={`grids[${gridIndex}].price`}
                      label="Preço"
                      type="number"
                      step="0.01"
                      placeholder="Ex.: 632"
                    />
                    <Input
                      name={`grids[${gridIndex}].discount`}
                      label="Desconto"
                      type="number"
                      step="0.01"
                      placeholder="Ex.: 50"
                      suffix="%"
                    />
                  </div>
                  <div>
                    <Input
                      name={`grids[${gridIndex}].minimumStock`}
                      type="number"
                      step="0.01"
                      placeholder="Ex.: 5"
                      label="Estoque mínimo"
                    />
                    <div style={{ display: 'flex', flex: 1, gap: 5 }}>
                      <Input
                        name={`grids[${gridIndex}].currentStock`}
                        type="number"
                        step="0.01"
                        placeholder="Ex.: 80"
                        label="Estoque atual"
                        disabled={typeof grid.id === 'number'}
                      />
                      {typeof grid.id === 'number' && (
                        <HelpButton
                          type="button"
                          onClick={() =>
                            handleToggleCanChangeCurrentStock(grid.id)
                          }
                        >
                          Alterar
                        </HelpButton>
                      )}
                    </div>
                  </div>
                  {grid.canChangeCurrentStock ? (
                    <div>
                      <CustomInput
                        isLoading={codeIsLoading[gridIndex]}
                        codeIsValid={codeIsValid[gridIndex]}
                        name={`grids[${gridIndex}].code`}
                        label="Código PDV"
                        placeholder="Ex.: 632"
                        onChange={(e) => {
                          reqDebouce(e.target.value, gridIndex);
                        }}
                      />
                      <Input
                        name={`grids[${gridIndex}].addStock`}
                        type="number"
                        step="0.01"
                        placeholder="Ex.: 80"
                        label="Alterar estoque"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div>
                      <CustomInput
                        isLoading={codeIsLoading[gridIndex]}
                        codeIsValid={codeIsValid[gridIndex]}
                        name={`grids[${gridIndex}].code`}
                        label="Código PDV"
                        placeholder="Ex.: 632"
                        onChange={(e) => {
                          reqDebouce(e.target.value, gridIndex);
                        }}
                      />
                    </div>
                  )}
                </SizeItemBody>
              </SizeItem>
            ))}
        </SizesList>
      )}
    </Container>
  );
};

export default Sizes;
